import React from "react";
import { Box, Container, Typography } from "@mui/material";
import TransitionImg from "../assets/Transition.png";
import { Helmet } from "react-helmet";

export default function TransitionSection() {
  return (
    <>
      <Helmet>
        <title>Transition Management - Namami India Support Services</title>
        <meta
          name="description"
          content="Seamless transition management by Namami India — ensuring smooth operational handovers, process optimization, and business continuity across projects."
        />
        <meta
          name="keywords"
          content="Namami India, transition management, business continuity, facility handover, process management, operations"
        />
        <meta property="og:title" content="Transition Management - Namami India" />
        <meta
          property="og:description"
          content="Our expert transition management services help businesses achieve smooth operational shifts and process excellence."
        />
        <meta property="og:image" content="https://www.namamiindia.co.in/assets/namami-logo.png" />
        <meta property="og:url" content="https://www.namamiindia.co.in/transition" />
        <link rel="canonical" href="https://www.namamiindia.co.in/transition" />
      </Helmet>

      {/* === Section 1: Main Introduction === */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: "#fff" }}>
        <Container
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 6, md: 10 },
          }}
        >
          {/* === Left Text === */}
          <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" }, maxWidth: 650 }}>
            <Typography
              variant="h3"
              fontWeight={900}
              mb={2}
              sx={{
                background: "linear-gradient(90deg, #1565c0, #004ba0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Transition Management
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.8, fontSize: "1.08rem", mb: 3 }}
            >
              Transition is not merely a shift between construction and operations—it’s the
              transformation of a blueprint into a living, breathing, efficient space. At{" "}
              <strong>Namami India</strong>, we understand that the smallest details during this
              phase determine the long-term success of a facility. Our transition experts bring
              precision, process, and purpose together to ensure that every project moves from
              completion to operation with seamless continuity.
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.8, mb: 3 }}
            >
              The process begins right from the fit-out stage — where our team inspects, tests, and
              fine-tunes every system, from HVAC and fire safety to electrical and plumbing
              mechanisms. This ensures your building is not just completed but operationally ready
              and compliant with all statutory norms.
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.8 }}
            >
              Our methodical approach focuses on documentation, training, and technology-assisted
              audits — ensuring that your transition process is structured, traceable, and built
              for long-term sustainability.
            </Typography>
          </Box>

          {/* === Right Image === */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={TransitionImg}
              alt="Transition Management"
              sx={{
                width: { xs: "85%", sm: "70%", md: "95%" },
                maxWidth: 520,
                borderRadius: 0,
                boxShadow: "none",
                objectFit: "contain",
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* === Section 2: Inverse Hero Section === */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: "linear-gradient(180deg, #004ba0, #002d6b)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={800} mb={3}>
            From Blueprint to Reality — Every Detail Matters
          </Typography>
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              fontSize: "1.1rem",
              opacity: 0.95,
            }}
          >
            Transition is where vision meets performance. We bridge the gap between design and
            delivery by ensuring that all systems — mechanical, electrical, safety, and
            administrative — are perfectly aligned. Our dedicated handover teams collaborate
            closely with facility management staff, empowering them through real-time knowledge
            transfer and documentation.
            <br />
            <br />
            This collaborative, people-first approach ensures zero disruption, smooth handovers, and
            operational excellence from day one. It’s not just management; it’s transformation with
            accountability and care.
          </Typography>
        </Container>
      </Box>

      {/* === Section 3: Deep Process Detailing === */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: "#f8f9fa" }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            fontWeight={800}
            mb={4}
            textAlign="center"
            sx={{
              background: "linear-gradient(90deg, #1565c0, #004ba0)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Transition Framework
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.9, fontSize: "1.05rem", maxWidth: 900, mx: "auto", textAlign: "center" }}
          >
            We adopt a structured, multi-phase framework that integrates <strong>technical
            precision, human collaboration, and strategic documentation</strong>. From site
            inspections and snagging to system verifications, every task is guided by
            international standards and best practices. Our experts deliver not just a building
            ready for operations — but a foundation ready for excellence.
            <br />
            <br />
            Whether you’re managing a commercial complex, IT campus, or institutional facility, our
            transition management services ensure a future-ready operational environment that’s safe,
            compliant, and efficient.
          </Typography>
        </Container>
      </Box>
    </>
  );
}
